import { DASHBOARD_TOUR_KEY, DASHBOARD_TOUR_STEPS, VwTourStep } from './tour.service';

export interface VwPageTourEntry {
  matches: (url: string) => boolean;
  key: string;
  steps: VwTourStep[];
}

// Matches a URL against one of several module prefixes, and (if given) one of
// several leaf path segments under that prefix. Two things this exists for:
//  - Several list-style pages are the module's default ('' path) route AND
//    also have a named alias (e.g. /home/officeStaff and
//    /home/officeStaff/staffList both render the same component) — an empty
//    "rest" after the prefix counts as a match without needing a leaf.
//  - The same leaf name (e.g. "currentVisitor", "staffHistory") is reused
//    across otherwise-unrelated modules (employee/resident/guest,
//    officeStaff/staff), and the same concept (guest vs. visitor, service
//    provider vs. contractor) is reachable under two different top-level
//    prefixes — so both the prefix and the leaf need to be checked together,
//    with either side allowed to be a short list of alternatives.
function under(prefixes: string | string[], leaves?: string | string[]): (url: string) => boolean {
  const prefixList = Array.isArray(prefixes) ? prefixes : [prefixes];
  const leafList = leaves ? (Array.isArray(leaves) ? leaves : [leaves]) : null;
  return (url: string) =>
    prefixList.some(prefix => {
      const at = url.indexOf(prefix);
      if (at === -1) {
        return false;
      }
      if (!leafList) {
        return true;
      }
      const rest = url.slice(at + prefix.length).split('?')[0];
      return rest === '' || rest === '/' || leafList.some(leaf => rest.indexOf(leaf) !== -1);
    });
}

const GUEST_LIST_TOUR_KEY = 'tour.guest-list';
const GUEST_LIST_TOUR_STEPS: VwTourStep[] = [
  { key: 'guest-search', title: 'Find a visitor fast', body: 'Type a name, contact or vehicle number here to filter the list as you type.' },
  { key: 'guest-table', title: 'Visitor records', body: 'Every visitor for this property, with check-in/out times, vehicle and status at a glance.' },
  { key: 'guest-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const USER_LIST_TOUR_KEY = 'tour.user-list';
const USER_LIST_TOUR_STEPS: VwTourStep[] = [
  { key: 'user-search', title: 'Find a user fast', body: 'Search by name, username or email to filter the list as you type.' },
  { key: 'user-add-btn', title: 'Add a new user', body: 'Create a new user account and assign it a role/group here.' },
  { key: 'user-table', title: 'User accounts', body: 'Everyone with access to this property, and who registered them.' },
];

// ---------------------------------------------------------------------------
// Administrator / Flagged / Blacklist / Rejected
// ---------------------------------------------------------------------------

const GROUPLIST_STEPS: VwTourStep[] = [
  { key: 'groupList-search', title: 'Find a group fast', body: 'Type a group name to filter the list as you type.' },
  { key: 'groupList-add-btn', title: 'Add a new group', body: 'Create a new user group and assign it a type here.' },
  { key: 'groupList-table', title: 'User groups', body: 'Every group for this property, with type, creator and active status.' },
  { key: 'groupList-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const CONFIGURATION_STEPS: VwTourStep[] = [
  { key: 'configuration-table', title: 'Feature toggles', body: 'Turn property features on or off and set values like trespasser time, QR expiry days and field configuration.' },
  { key: 'configuration-save-btn', title: 'Save your changes', body: 'Apply the feature and field configuration changes you made above.' },
];

const HIERARCHYSETUP_STEPS: VwTourStep[] = [
  { key: 'hierarchySetup-add-btn', title: 'Add a hierarchy level', body: 'Create a new level in the property hierarchy (e.g. block, floor).' },
  { key: 'hierarchySetup-table', title: 'Property hierarchy', body: "The levels that make up this property's structure, from top level down." },
];

const RESETPASSWORDREQUEST_STEPS: VwTourStep[] = [
  { key: 'resetPasswordRequest-table', title: 'Password reset requests', body: 'Users, their role and active status, with when the request was made.' },
  { key: 'resetPasswordRequest-approve-btn', title: 'Reset a password', body: 'Set a new password for this user to resolve their request.' },
];

const ENTRYPOINT_STEPS: VwTourStep[] = [
  { key: 'entryPoint-add-btn', title: 'Add an entry point', body: 'Map a device user to a gate or multi check-in point.' },
  { key: 'entryPoint-table', title: 'Entry point mappings', body: 'Device users, their mapped gate or check-in point, and active hours.' },
];

const GATEMASTER_STEPS: VwTourStep[] = [
  { key: 'gateMaster-add-btn', title: 'Add a gate', body: 'Register a new gate name for this property.' },
  { key: 'gateMaster-table', title: 'Gates', body: 'All gates configured for this property.' },
];

const DEVICEBINDING_STEPS: VwTourStep[] = [
  { key: 'deviceBinding-table', title: 'Device binding status', body: 'Users eligible for mobile binding, their status and bound device — grant or revoke access here.' },
];

const REGISTRATIONCODE_STEPS: VwTourStep[] = [
  { key: 'registrationCode-add-btn', title: 'Generate a registration code', body: 'Create a new code for device or user self-registration.' },
  { key: 'registrationCode-table', title: 'Registration codes', body: 'Active and used codes, their role, usage count and expiry date.' },
];

const REGISTRATIONREQUEST_STEPS: VwTourStep[] = [
  { key: 'registrationRequest-approve-btn', title: 'Approve all pending requests', body: 'Bulk-approve every pending self-registration request at once.' },
  { key: 'registrationRequest-table', title: 'Registration requests', body: 'Name, role, contact and status for everyone requesting access.' },
];

const FLAGGED_STEPS: VwTourStep[] = [
  { key: 'flagged-table', title: 'Flagged visitors', body: 'Visitors flagged for review, with reason, who flagged them and when.' },
  { key: 'flagged-unflag-btn', title: 'Clear a flag', body: 'Remove the flag once the concern has been resolved.' },
];

const UNFLAGGED_STEPS: VwTourStep[] = [
  { key: 'unflagged-table', title: 'Unflagged visitors', body: 'Visitors whose flags were cleared, with who cleared them and the original reason.' },
];

const BLACKLIST_STEPS: VwTourStep[] = [
  { key: 'blacklist-table', title: 'Blacklisted visitors', body: 'Blocked visitors, their reason and who blocked them — remove them from here.' },
];

const BLACKLISTHISTORY_STEPS: VwTourStep[] = [
  { key: 'blacklistHistory-table', title: 'Blacklist history', body: 'A record of every visitor ever blacklisted, including ones since reactivated.' },
];

const REJECTEDVISITOR_STEPS: VwTourStep[] = [
  { key: 'rejectedVisitor-table', title: 'Rejected visitors', body: 'Visitors whose check-in was rejected, with reason, rejector and booked details.' },
];

// ---------------------------------------------------------------------------
// OfficeStaff / household staff / HouseHelp / ScrapAgent
// ---------------------------------------------------------------------------

const OFFICESTAFF_STEPS: VwTourStep[] = [
  { key: 'officeStaff-search', title: 'Find staff fast', body: 'Type a name, contact or ID number here to filter the staff list as you type.' },
  { key: 'officeStaff-table', title: 'Staff records', body: 'Every registered staff member, with employment type, working hours, active status and images.' },
  { key: 'officeStaff-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const OFFICECURRENTSTAFF_STEPS: VwTourStep[] = [
  { key: 'officeCurrentStaff-table', title: 'Staff currently on-site', body: 'See who is checked in right now, their check-in time and entered vehicle number.' },
  { key: 'officeCurrentStaff-checkout-btn', title: 'Check a staff member out', body: 'Click Check-out to record when this staff member leaves the premises.' },
];

const OFFICESTAFFHISTORY_STEPS: VwTourStep[] = [
  { key: 'officeStaffHistory-search', title: 'Find a record fast', body: 'Type a name, contact or ID number here to filter the history as you type.' },
  { key: 'officeStaffHistory-table', title: 'Check-in/out history', body: 'A full history of staff visits, with check-in and check-out timestamps and vehicle numbers.' },
];

const OFFICEREGISTEREDSTAFF_STEPS: VwTourStep[] = [
  { key: 'officeRegisteredStaff-table', title: 'Registered staff', body: 'Everyone registered as staff, including who registered them and on what date.' },
];

const OFFICEEXPECTEDSTAFF_STEPS: VwTourStep[] = [
  { key: 'officeExpectedStaff-table', title: 'Scheduled staff', body: 'Staff scheduled on-site today, with a Check-In action to confirm their arrival.' },
];

const OFFICESTAFFIMPORT_STEPS: VwTourStep[] = [
  { key: 'officeStaffImport-upload', title: 'Upload a staff sheet', body: 'Choose a spreadsheet file here to bulk-import staff records.' },
  { key: 'officeStaffImport-submit-btn', title: 'Submit the import', body: 'Once your file is selected, click Submit to process the import.' },
];

const HOUSEHOLDSTAFF_STEPS: VwTourStep[] = [
  { key: 'householdStaff-search', title: 'Find staff fast', body: 'Type a name, contact or ID number here to filter the domestic staff list as you type.' },
  { key: 'householdStaff-add-btn', title: 'Add a new staff member', body: 'Register a new domestic staff member here.' },
  { key: 'householdStaff-table', title: 'Domestic staff records', body: 'Every domestic staff member, their employment type, working hours and active status.' },
];

const HOUSEHOLDCURRENTSTAFF_STEPS: VwTourStep[] = [
  { key: 'householdCurrentStaff-table', title: 'Domestic staff on-site', body: 'See which domestic staff are currently checked in, with their profile and status.' },
];

const HOUSEHOLDSTAFFHISTORY_STEPS: VwTourStep[] = [
  { key: 'householdStaffHistory-table', title: 'Domestic staff history', body: 'A full history of domestic staff visits, with check-in and check-out timestamps.' },
];

const HOUSEHOLDEXPECTEDSTAFF_STEPS: VwTourStep[] = [
  { key: 'householdExpectedStaff-table', title: 'Scheduled domestic staff', body: 'Domestic staff scheduled today, ready to be checked in on arrival.' },
];

const HOUSEHOLDSTAFFIMPORT_STEPS: VwTourStep[] = [
  { key: 'householdStaffImport-upload', title: 'Upload a staff sheet', body: 'Choose a spreadsheet file here to bulk-import domestic staff records.' },
  { key: 'householdStaffImport-submit-btn', title: 'Submit the import', body: 'Once your file is selected, click Submit to process the import.' },
];

const HOUSEHELP_STEPS: VwTourStep[] = [
  { key: 'houseHelp-search', title: 'Find house help fast', body: 'Type a name, ID number or contact here to filter the list as you type.' },
  { key: 'houseHelp-table', title: 'House help records', body: 'Every registered house help entry, with registration and contact details.' },
];

const HOUSEHELPTRESPASSER_STEPS: VwTourStep[] = [
  { key: 'houseHelpTrespasser-table', title: 'Trespasser house help', body: 'House help flagged as trespassers, with check-in time and elapsed time on-site.' },
];

const SCRAPAGENT_STEPS: VwTourStep[] = [
  { key: 'scrapAgent-add-btn', title: 'Add a scrap agent', body: 'Register a new scrap agent here.' },
  { key: 'scrapAgent-table', title: 'Scrap agents', body: 'All registered scrap agents, with edit and delete actions.' },
];

// ---------------------------------------------------------------------------
// ServiceProvider / employee
// ---------------------------------------------------------------------------

const SERVICEPROVIDER_STEPS: VwTourStep[] = [
  { key: 'serviceProvider-search', title: 'Find a service provider fast', body: 'Type a name, identity number or vehicle number here to filter the list as you type.' },
  { key: 'serviceProvider-add-btn', title: 'Add a contractor', body: 'Register a new service provider or contractor and start their visit here.' },
  { key: 'serviceProvider-table', title: 'Service provider records', body: 'Every service provider for this property, with check-in/out times, vehicle and status at a glance.' },
  { key: 'serviceProvider-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const BOOKEDSERVICEPROVIDER_STEPS: VwTourStep[] = [
  { key: 'bookedServiceProvider-table', title: 'Booked service providers', body: 'Service providers booked in advance, with their booked date and who booked them.' },
  { key: 'bookedServiceProvider-checkin-btn', title: 'Check in a booking', body: 'When a booked service provider arrives, check them in here.' },
];

const TRESPASSERSERVICEPROVIDER_STEPS: VwTourStep[] = [
  { key: 'trespasserServiceProvider-table', title: 'Trespasser service providers', body: 'Service providers still on site past their host checkout time, with elapsed time flagged.' },
];

const RECURRENTSERVICEPROVIDER_STEPS: VwTourStep[] = [
  { key: 'recurrentServiceProvider-table', title: 'Recurring service providers', body: 'Service providers who visit regularly, with their visit count and a shortcut to view or generate a QR code.' },
];

const RECURRENTSPDTL_STEPS: VwTourStep[] = [
  { key: 'recurrentSPDtl-detail', title: 'Recurring visit history', body: 'Every past visit for this recurring service provider, with check-in and check-out times.' },
];

const HOSTRECURRENTSP_STEPS: VwTourStep[] = [
  { key: 'hostRecurrentSP-table', title: 'Your recurring service providers', body: 'Service providers who recur for you, with their check-in and check-out times.' },
];

const EMPLOYEE_STEPS: VwTourStep[] = [
  { key: 'employee-search', title: 'Find a staff member fast', body: 'Type a name, staff ID or contact number here to filter the list as you type.' },
  { key: 'employee-add-btn', title: 'Add a staff member', body: 'Register a new staff member and their details here.' },
  { key: 'employee-table', title: 'Staff records', body: 'Every staff member for this property, with designation, contact details and status.' },
  { key: 'employee-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const EMPLOYEECURRENTVISITOR_STEPS: VwTourStep[] = [
  { key: 'employeeCurrentVisitor-table', title: 'Current visitors', body: 'Visitors currently checked in to see you, with their check-in time and vehicle number.' },
];

const EMPLOYEEEXPECTEDVISITOR_STEPS: VwTourStep[] = [
  { key: 'employeeExpectedVisitor-table', title: 'Booked visitors', body: "Visitors you've pre-registered, with booked date, VIP status and QR code actions." },
];

const EMPLOYEEVISITORHISTORY_STEPS: VwTourStep[] = [
  { key: 'employeeVisitorHistory-search', title: 'Find a visit fast', body: 'Type a name to filter your visitor history as you type.' },
  { key: 'employeeVisitorHistory-table', title: 'Visitor history', body: 'Every past visitor who came to see you, with check-in/out times and visit status.' },
];

const EMPLOYEEIMPORT_STEPS: VwTourStep[] = [
  { key: 'employeeImport-upload', title: 'Choose a file', body: 'Select the spreadsheet with your staff records to import.' },
  { key: 'employeeImport-submit-btn', title: 'Submit the import', body: 'Upload the file to create all the staff records at once.' },
];

const EMPLOYEEIMPORTGUEST_STEPS: VwTourStep[] = [
  { key: 'employeeImportGuest-upload', title: 'Choose a file', body: 'Select the filled-in Excel template with your guest data to import.' },
  { key: 'employeeImportGuest-submit-btn', title: 'Submit the import', body: 'Upload the file to create all the guest records at once.' },
];

// ---------------------------------------------------------------------------
// Guest / visitor (remaining pages — the base list already has a tour)
// ---------------------------------------------------------------------------

const RECURRENTGUEST_STEPS: VwTourStep[] = [
  { key: 'recurrentGuest-table', title: 'Recurring visitors', body: 'Everyone who visits regularly, with their visit count and a quick way to view details, generate or download their QR code.' },
];

const BOOKEDGUEST_STEPS: VwTourStep[] = [
  { key: 'bookedGuest-table', title: 'Booked visitors', body: 'Visitors booked soon, with who booked them, booked time, booked vehicle and VIP status.' },
  { key: 'bookedGuest-checkin-btn', title: 'Check in a booked visitor', body: 'When a booked visitor arrives, use Check-in here to verify their identity and complete arrival.' },
];

const TRESPASSERGUEST_STEPS: VwTourStep[] = [
  { key: 'trespasserGuest-table', title: 'Trespasser visitors', body: 'Visitors who overstayed — check-in time, host check-out time and elapsed time since they were due to leave.' },
];

const GUESTDETAILS_STEPS: VwTourStep[] = [
  { key: 'guestDetails-detail', title: 'Visitor details', body: 'Full record for this visitor — identity, contact, check-in/out times and vehicle info, plus flag or blacklist actions.' },
];

const CURRENTGUEST_STEPS: VwTourStep[] = [
  { key: 'currentGuest-table', title: 'Current visitors', body: 'Everyone checked in right now, with check-in time, vehicle number and current status.' },
  { key: 'currentGuest-checkout-btn', title: 'Check out a visitor', body: 'Use Check-out here once a visitor is ready to leave.' },
];

const HOSTRECURRENTGUEST_STEPS: VwTourStep[] = [
  { key: 'hostRecurrentGuest-table', title: 'Your recurring visitors', body: 'Visitors who see you regularly, with their document details, visit count and QR code actions.' },
];

const RECURRENTGUESTDTL_STEPS: VwTourStep[] = [
  { key: 'recurrentGuestDtl-detail', title: 'Recurring visitor history', body: 'Every visit this recurring visitor has made, with check-in and check-out times.' },
];

const SELFCHECKINGENERATE_STEPS: VwTourStep[] = [
  { key: 'selfCheckInGenerate-qr', title: 'Your QR code', body: 'The generated self check-in QR code appears here — share or display it for the visitor to scan.' },
  { key: 'selfCheckInGenerate-generate-btn', title: 'Generate the QR code', body: "Once you've set the options, click Generate QR to create the self check-in code." },
];

// ---------------------------------------------------------------------------
// Resident module
// ---------------------------------------------------------------------------

const RESIDENTLIST_STEPS: VwTourStep[] = [
  { key: 'residentList-search', title: 'Find a resident fast', body: 'Type a name, contact number or vehicle number here to filter the list as you type.' },
  { key: 'residentList-add-btn', title: 'Add a new resident', body: 'Register a new resident for this household here.' },
  { key: 'residentList-table', title: 'Resident records', body: 'Every resident in your household, with type, contact numbers and active status.' },
  { key: 'residentList-pagination', title: 'Paging through records', body: 'Long lists are split into pages — use these controls to move through them.' },
];

const RESIDENTFAMILYLIST_STEPS: VwTourStep[] = [
  { key: 'residentFamilyList-table', title: 'Family members', body: 'Everyone registered under your household, with contact, relationship and QR code access.' },
  { key: 'residentFamilyList-add-btn', title: 'Add a family member', body: 'Register a new family member to your household here.' },
];

const EXPECTEDSERVICEPROVIDERRESIDENT_STEPS: VwTourStep[] = [
  { key: 'expectedServiceProviderResident-table', title: 'Booked service providers', body: "Service providers you've pre-approved, with their booked date, mode and vehicle number." },
];

const CURRENTSERVICEPROVIDERRESIDENT_STEPS: VwTourStep[] = [
  { key: 'currentServiceProviderResident-table', title: 'Service providers on-site', body: 'Service providers currently checked in for your household, with check-in time and vehicle number.' },
];

const SERVICEPROVIDERHISTORYRESIDENT_STEPS: VwTourStep[] = [
  { key: 'serviceProviderHistoryResident-table', title: 'Service provider history', body: 'Past visits from service providers, with check-in/out times, vehicle number and status.' },
];

const RESIDENTDETAILS_STEPS: VwTourStep[] = [
  { key: 'residentDetails-detail', title: 'Resident details', body: 'Household resident records with name, type, primary number and photo, all in one place.' },
];

const EXPECTEDRESIDENT_STEPS: VwTourStep[] = [
  { key: 'expectedResident-table', title: 'Scheduled residents', body: 'Residents scheduled to arrive, with a quick Check-In action for each.' },
];

const CURRENTRESIDENT_STEPS: VwTourStep[] = [
  { key: 'currentResident-table', title: 'Residents on-site', body: 'Residents currently checked in, with check-in time, vehicle number and a Check-Out action.' },
];

const RESIDENTHISTORY_STEPS: VwTourStep[] = [
  { key: 'residentHistory-search', title: 'Find a visit fast', body: 'Type a name or contact number here to filter the history list as you type.' },
  { key: 'residentHistory-table', title: 'Resident visit history', body: 'Past check-ins with check-in/out times and vehicle number for every resident.' },
];

const VEHICLEINFORESIDENT_STEPS: VwTourStep[] = [
  { key: 'vehicleInfoResident-table', title: 'Registered vehicles', body: 'Vehicles registered to your household, with type and registration date.' },
  { key: 'vehicleInfoResident-add-btn', title: 'Register a vehicle', body: 'Add a new vehicle to your household here.' },
];

const SELFSERVICELINK_STEPS: VwTourStep[] = [
  { key: 'selfServiceLink-generate-btn', title: 'Generate a self-service link', body: 'Create a shareable link so a visitor or family member can register themselves without your involvement.' },
  { key: 'selfServiceLink-list', title: 'Generated links', body: "Every self-service link you've created, with usage, expiry status and description." },
];

const SELFGUESTDETAILS_STEPS: VwTourStep[] = [
  { key: 'selfGuestDetails-table', title: 'Self-registered guests', body: 'Guests who registered themselves via a self-service link, awaiting your Accept or Reject.' },
];

// ---------------------------------------------------------------------------
// Reports / premise / notifications / panic / vehicle management / account
// ---------------------------------------------------------------------------

const EXITEDVISITOR_STEPS: VwTourStep[] = [
  { key: 'exitedVisitor-daterange', title: 'Pick a date range', body: 'Set the from/to dates (and visitor type) to scope which exited visitors show up.' },
  { key: 'exitedVisitor-generate-btn', title: 'Run the search', body: 'Click Search to pull exited visitor records for the range and filters above.' },
  { key: 'exitedVisitor-result', title: 'Exited visitor records', body: 'Results appear here — export to PDF or Excel once you have the list you need.' },
];

const AUDITTRIALREPORT_STEPS: VwTourStep[] = [
  { key: 'auditTrialReport-daterange', title: 'Pick a date range', body: 'Set the from/to dates and optionally a user to narrow the audit trail.' },
  { key: 'auditTrialReport-generate-btn', title: 'Run the search', body: 'Click Search to fetch matching audit trail entries.' },
  { key: 'auditTrialReport-result', title: 'Audit trail entries', body: 'Every logged action, activity, timestamp and the user who performed it.' },
];

const VISITORVEHICLEREPORT_STEPS: VwTourStep[] = [
  { key: 'visitorVehicleReport-daterange', title: 'Pick a date range', body: 'Set the from/to dates and visitor type to filter vehicle activity.' },
  { key: 'visitorVehicleReport-generate-btn', title: 'Run the search', body: 'Click Search to fetch vehicle check-in/out records for the range.' },
  { key: 'visitorVehicleReport-result', title: 'Vehicle records', body: 'Vehicle numbers, models and check-in/out times, exportable to PDF or Excel.' },
];

const DAILYGUESTREPORT_STEPS: VwTourStep[] = [
  { key: 'dailyGuestReport-form', title: 'Choose the report date', body: "Pick a date and click Fetch to load that day's guest visits by department." },
  { key: 'dailyGuestReport-save-btn', title: 'Notify department heads', body: "Send the daily guest report straight to each HOD's email." },
];

const HODLIST_STEPS: VwTourStep[] = [
  { key: 'hodList-table', title: 'Heads of Department', body: 'Every registered HOD with their email and department, used for daily report routing.' },
];

const DYNAMICREPORT_STEPS: VwTourStep[] = [
  { key: 'dynamicReport-filters', title: 'Build your filter', body: 'Combine date range, purpose of visit, guest type and vehicle number to slice the data your way.' },
  { key: 'dynamicReport-generate-btn', title: 'Run the search', body: 'Click Search once your filters are set to pull matching records.' },
  { key: 'dynamicReport-result', title: 'Filtered results', body: 'Matching visits with guest type, purpose, property and check-in/out details.' },
];

const SOCIETY_STEPS: VwTourStep[] = [
  { key: 'society-search', title: 'Find a property fast', body: 'Search here to filter the property list as you type.' },
  { key: 'society-add-btn', title: 'Add a property', body: 'Register a new property at the selected hierarchy level.' },
  { key: 'society-table', title: 'Property records', body: 'Every property at this level, its owner/tenant and active status.' },
];

const CHECKINNOTIFICATION_STEPS: VwTourStep[] = [
  { key: 'checkInNotification-table', title: 'Check-in requests', body: 'Pending and past check-in notifications, with reason, host and status — accept or reject from here.' },
];

const PANICNOTIFICATION_STEPS: VwTourStep[] = [
  { key: 'panicNotification-table', title: 'Panic alerts', body: 'Every panic button press, with the device, location and description, most recent first.' },
];

const VEHICLELIST_STEPS: VwTourStep[] = [
  { key: 'vehicleList-add-btn', title: 'Add a vehicle', body: 'Register a new commercial vehicle by number and make.' },
  { key: 'vehicleList-table', title: 'Vehicle records', body: 'Every registered vehicle, with its QR code for gate scanning.' },
];

const VEHICLEDETAIL_STEPS: VwTourStep[] = [
  { key: 'vehicleDetail-detail', title: 'Vehicle activity log', body: 'Check-in/out times and driver names for every vehicle movement recorded.' },
];

const EXPECTEDVEHICLE_STEPS: VwTourStep[] = [
  { key: 'expectedVehicle-table', title: 'Booked vehicles', body: 'Vehicles awaiting check-in — use the Check-In action once one arrives.' },
];

const CURRENTVEHICLE_STEPS: VwTourStep[] = [
  { key: 'currentVehicle-table', title: 'Vehicles on premises', body: 'Vehicles currently checked in — use Check-Out once one leaves.' },
];

const IMPORTVEHICLE_STEPS: VwTourStep[] = [
  { key: 'importVehicle-upload', title: 'Choose your file', body: 'Select a spreadsheet of vehicles to bulk-import — download the sample sheet first if unsure of the format.' },
  { key: 'importVehicle-submit-btn', title: 'Run the import', body: 'Submit the file to register all vehicles in it at once.' },
];

const PROFILE_STEPS: VwTourStep[] = [
  { key: 'profile-form', title: 'Your account details', body: 'Update your name, email, gender, contact number and address here.' },
  { key: 'profile-save-btn', title: 'Save your changes', body: "Click Update once you're done editing your profile." },
];

const OCRAPIKEY_STEPS: VwTourStep[] = [
  { key: 'ocrAPIKey-add-btn', title: 'Generate an OCR key', body: "Create a new OCR API key for a property that doesn't have one yet." },
  { key: 'ocrAPIKey-table', title: 'OCR API keys', body: "Every property's OCR key, its creator and creation date — renew or check usage from here." },
];

const OCRSTATS_STEPS: VwTourStep[] = [
  { key: 'ocrStats-table', title: 'OCR usage at a glance', body: 'Requests used, remaining quota and key status, with a live usage progress bar.' },
];

const MOBILEBINDINGLICENSE_STEPS: VwTourStep[] = [
  { key: 'mobileBindingLicense-form', title: 'Set the mobile license', body: 'Pick a property, set its license expiry date, and optionally cap the number of QR codes it can generate.' },
];

// New page tours are added here as they're built — the app shell (default
// layout) drives auto-start and "replay" purely off this list, keyed by
// route, so no individual page component needs its own trigger wiring.
// Order matters where one URL is a substring of another (e.g.
// "currentStaffHistory" contains "staffHistory") — the more specific entry
// must come first since `findPageTour` takes the first match.
export const PAGE_TOURS: VwPageTourEntry[] = [
  { matches: url => url.indexOf('/home/dashboard') !== -1, key: DASHBOARD_TOUR_KEY, steps: DASHBOARD_TOUR_STEPS },
  { matches: url => /\/home\/guest(\/(guestDetails|visitorDetails))?($|\?)/.test(url), key: GUEST_LIST_TOUR_KEY, steps: GUEST_LIST_TOUR_STEPS },
  { matches: url => url.indexOf('/home/adminstrator/userList') !== -1, key: USER_LIST_TOUR_KEY, steps: USER_LIST_TOUR_STEPS },

  // Administrator
  { matches: under('/home/adminstrator/', 'groupList'), key: 'tour.group-list', steps: GROUPLIST_STEPS },
  { matches: under('/home/adminstrator/', 'configuration'), key: 'tour.configuration', steps: CONFIGURATION_STEPS },
  { matches: under('/home/adminstrator/', 'hierarchySetup'), key: 'tour.hierarchy-setup', steps: HIERARCHYSETUP_STEPS },
  { matches: under('/home/adminstrator/', 'resetPasswordRequest'), key: 'tour.reset-password-request', steps: RESETPASSWORDREQUEST_STEPS },
  { matches: under('/home/adminstrator/', 'entryPoint'), key: 'tour.entry-point', steps: ENTRYPOINT_STEPS },
  { matches: under('/home/adminstrator/', 'gateMaster'), key: 'tour.gate-master', steps: GATEMASTER_STEPS },
  { matches: under('/home/adminstrator/', 'deviceBinding'), key: 'tour.device-binding', steps: DEVICEBINDING_STEPS },
  { matches: under('/home/adminstrator/', 'registrationCode'), key: 'tour.registration-code', steps: REGISTRATIONCODE_STEPS },
  { matches: under('/home/adminstrator/', 'registrationRequest'), key: 'tour.registration-request', steps: REGISTRATIONREQUEST_STEPS },

  // Flagged / Blacklist / Rejected
  { matches: under('/home/flagged', 'unflaggedVisitor'), key: 'tour.unflagged', steps: UNFLAGGED_STEPS },
  { matches: under('/home/flagged', 'flaggedVisitor'), key: 'tour.flagged', steps: FLAGGED_STEPS },
  { matches: under('/home/blackList', 'blackListVisitor'), key: 'tour.blacklist', steps: BLACKLIST_STEPS },
  { matches: under('/home/blackList', 'blackListHistory'), key: 'tour.blacklist-history', steps: BLACKLISTHISTORY_STEPS },
  { matches: under('/home/rejected', 'visitor'), key: 'tour.rejected-visitor', steps: REJECTEDVISITOR_STEPS },

  // OfficeStaff (commercial staff)
  { matches: under('/home/officeStaff/', 'currentStaff'), key: 'tour.office-current-staff', steps: OFFICECURRENTSTAFF_STEPS },
  { matches: under('/home/officeStaff/', 'staffHistory'), key: 'tour.office-staff-history', steps: OFFICESTAFFHISTORY_STEPS },
  { matches: under('/home/officeStaff/', 'registeredStaff'), key: 'tour.office-registered-staff', steps: OFFICEREGISTEREDSTAFF_STEPS },
  { matches: under('/home/officeStaff/', 'expectedStaff'), key: 'tour.office-expected-staff', steps: OFFICEEXPECTEDSTAFF_STEPS },
  { matches: under('/home/officeStaff/', 'importStaff'), key: 'tour.office-staff-import', steps: OFFICESTAFFIMPORT_STEPS },
  { matches: under('/home/officeStaff/', 'staffList'), key: 'tour.office-staff', steps: OFFICESTAFF_STEPS },
  { matches: under('/home/officeStaff'), key: 'tour.office-staff', steps: OFFICESTAFF_STEPS },

  // staff (household/domestic staff)
  { matches: under('/home/staff/', 'currentStaffHistory'), key: 'tour.household-current-staff', steps: HOUSEHOLDCURRENTSTAFF_STEPS },
  { matches: under('/home/staff/', 'staffHistory'), key: 'tour.household-staff-history', steps: HOUSEHOLDSTAFFHISTORY_STEPS },
  { matches: under('/home/staff/', 'expectedStaffList'), key: 'tour.household-expected-staff', steps: HOUSEHOLDEXPECTEDSTAFF_STEPS },
  { matches: under('/home/staff/', 'importStaff'), key: 'tour.household-staff-import', steps: HOUSEHOLDSTAFFIMPORT_STEPS },
  { matches: under('/home/staff/', 'staffList'), key: 'tour.household-staff', steps: HOUSEHOLDSTAFF_STEPS },
  { matches: under('/home/staff'), key: 'tour.household-staff', steps: HOUSEHOLDSTAFF_STEPS },

  // HouseHelp
  { matches: under('/home/houseHelp/', 'trespasserHousehelp'), key: 'tour.house-help-trespasser', steps: HOUSEHELPTRESPASSER_STEPS },
  { matches: under('/home/houseHelp'), key: 'tour.house-help', steps: HOUSEHELP_STEPS },

  // ScrapAgentManagement
  { matches: under('/home/scrapAgent'), key: 'tour.scrap-agent', steps: SCRAPAGENT_STEPS },

  // ServiceProvider / Contractor
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], ['bookedServiceProvider', 'bookedContractor']), key: 'tour.booked-service-provider', steps: BOOKEDSERVICEPROVIDER_STEPS },
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], 'trespasserServiceProvider'), key: 'tour.trespasser-service-provider', steps: TRESPASSERSERVICEPROVIDER_STEPS },
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], ['recurrentServiceProvider', 'recurrentContractor']), key: 'tour.recurrent-service-provider', steps: RECURRENTSERVICEPROVIDER_STEPS },
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], 'recurrentSPDtl'), key: 'tour.recurrent-sp-dtl', steps: RECURRENTSPDTL_STEPS },
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], 'hostRecurrentSP'), key: 'tour.host-recurrent-sp', steps: HOSTRECURRENTSP_STEPS },
  { matches: under(['/home/serviceProvider/', '/home/contractor/'], ['serviceProviderList', 'contractorList']), key: 'tour.service-provider', steps: SERVICEPROVIDER_STEPS },
  { matches: under(['/home/serviceProvider', '/home/contractor']), key: 'tour.service-provider', steps: SERVICEPROVIDER_STEPS },

  // Employee
  { matches: under('/home/employee/', 'currentVisitor'), key: 'tour.employee-current-visitor', steps: EMPLOYEECURRENTVISITOR_STEPS },
  { matches: under('/home/employee/', 'expectedVisitor'), key: 'tour.employee-expected-visitor', steps: EMPLOYEEEXPECTEDVISITOR_STEPS },
  { matches: under('/home/employee/', 'visitorHistory'), key: 'tour.employee-visitor-history', steps: EMPLOYEEVISITORHISTORY_STEPS },
  { matches: under('/home/employee/', 'importEmployee'), key: 'tour.employee-import', steps: EMPLOYEEIMPORT_STEPS },
  { matches: under('/home/employee/', 'importGuest'), key: 'tour.employee-import-guest', steps: EMPLOYEEIMPORTGUEST_STEPS },
  { matches: under('/home/employee/', 'employeeDetails'), key: 'tour.employee', steps: EMPLOYEE_STEPS },
  { matches: under('/home/employee'), key: 'tour.employee', steps: EMPLOYEE_STEPS },

  // Guest / Visitor (remaining pages)
  { matches: under(['/home/guest/', '/home/visitor/'], ['recurrentGuest', 'recurrentVisitor']), key: 'tour.recurrent-guest', steps: RECURRENTGUEST_STEPS },
  { matches: under(['/home/guest/', '/home/visitor/'], ['bookedGuest', 'bookedVisitor']), key: 'tour.booked-guest', steps: BOOKEDGUEST_STEPS },
  { matches: under(['/home/guest/', '/home/visitor/'], ['trespasserGuest', 'trespasserVisitor']), key: 'tour.trespasser-guest', steps: TRESPASSERGUEST_STEPS },
  {
    matches: url => (url.indexOf('/home/guest/') !== -1 || url.indexOf('/home/visitor/') !== -1) && /\/(guestDetails|visitorDetails)\/\w+/.test(url),
    key: 'tour.guest-details',
    steps: GUESTDETAILS_STEPS,
  },
  { matches: under(['/home/guest/', '/home/visitor/'], ['currentGuest', 'currentVisitor']), key: 'tour.current-guest', steps: CURRENTGUEST_STEPS },
  { matches: under(['/home/guest/', '/home/visitor/'], ['hostRecurrentGuest', 'hostRecurrentVisitor']), key: 'tour.host-recurrent-guest', steps: HOSTRECURRENTGUEST_STEPS },
  { matches: under(['/home/guest/', '/home/visitor/'], 'recurrentGuestDtl'), key: 'tour.recurrent-guest-dtl', steps: RECURRENTGUESTDTL_STEPS },
  { matches: under(['/home/guest/', '/home/visitor/'], 'selfCheckInGenerate'), key: 'tour.self-check-in-generate', steps: SELFCHECKINGENERATE_STEPS },

  // Resident
  { matches: under('/home/resident/', 'residentFamilyList'), key: 'tour.resident-family-list', steps: RESIDENTFAMILYLIST_STEPS },
  { matches: under('/home/resident/', 'expectedServiceProvider'), key: 'tour.expected-service-provider-resident', steps: EXPECTEDSERVICEPROVIDERRESIDENT_STEPS },
  { matches: under('/home/resident/', 'currentServiceProvider'), key: 'tour.current-service-provider-resident', steps: CURRENTSERVICEPROVIDERRESIDENT_STEPS },
  { matches: under('/home/resident/', 'serviceProviderHostory'), key: 'tour.service-provider-history-resident', steps: SERVICEPROVIDERHISTORYRESIDENT_STEPS },
  { matches: under('/home/resident/', 'residentDetails'), key: 'tour.resident-details', steps: RESIDENTDETAILS_STEPS },
  { matches: under('/home/resident/', 'expectedResident'), key: 'tour.expected-resident', steps: EXPECTEDRESIDENT_STEPS },
  { matches: under('/home/resident/', 'currentResident'), key: 'tour.current-resident', steps: CURRENTRESIDENT_STEPS },
  { matches: under('/home/resident/', 'residentHistory'), key: 'tour.resident-history', steps: RESIDENTHISTORY_STEPS },
  { matches: under('/home/resident/', 'vehicleInfo'), key: 'tour.vehicle-info-resident', steps: VEHICLEINFORESIDENT_STEPS },
  { matches: under('/home/resident/', 'residentList'), key: 'tour.resident-list', steps: RESIDENTLIST_STEPS },
  { matches: under('/home/resident'), key: 'tour.resident-list', steps: RESIDENTLIST_STEPS },

  // Shared self-service pages (resident + employee both route here)
  { matches: under(['/home/resident/', '/home/employee/'], 'selfServiceLink'), key: 'tour.self-service-link', steps: SELFSERVICELINK_STEPS },
  { matches: under(['/home/resident/', '/home/employee/'], 'selfGuestDetails'), key: 'tour.self-guest-details', steps: SELFGUESTDETAILS_STEPS },

  // Reports
  { matches: under('/home/report/', 'auditReport'), key: 'tour.audit-trial-report', steps: AUDITTRIALREPORT_STEPS },
  { matches: under('/home/report/', 'vehicleReport'), key: 'tour.visitor-vehicle-report', steps: VISITORVEHICLEREPORT_STEPS },
  { matches: under('/home/report/', 'dailyGuestReport'), key: 'tour.daily-guest-report', steps: DAILYGUESTREPORT_STEPS },
  { matches: under('/home/report/', 'hodList'), key: 'tour.hod-list', steps: HODLIST_STEPS },
  { matches: under('/home/report/', 'dynamicReport'), key: 'tour.dynamic-report', steps: DYNAMICREPORT_STEPS },
  { matches: under('/home/report/', 'exitVistorReport'), key: 'tour.exited-visitor', steps: EXITEDVISITOR_STEPS },
  { matches: under('/home/report'), key: 'tour.exited-visitor', steps: EXITEDVISITOR_STEPS },

  // Premise / notifications / panic
  { matches: under('/home/premise'), key: 'tour.society', steps: SOCIETY_STEPS },
  { matches: under(['/home/notifications/', '/home/notification/', '/home/notifications', '/home/notification']), key: 'tour.check-in-notification', steps: CHECKINNOTIFICATION_STEPS },
  { matches: under('/home/panicNotification'), key: 'tour.panic-notification', steps: PANICNOTIFICATION_STEPS },

  // Vehicle management
  { matches: under('/home/vehicle/', 'vehicleDetail'), key: 'tour.vehicle-detail', steps: VEHICLEDETAIL_STEPS },
  { matches: under('/home/vehicle/', 'importVehicle'), key: 'tour.import-vehicle', steps: IMPORTVEHICLE_STEPS },
  { matches: under('/home/vehicle/', 'expectedVehicle'), key: 'tour.expected-vehicle', steps: EXPECTEDVEHICLE_STEPS },
  { matches: under('/home/vehicle/', 'currentVehicle'), key: 'tour.current-vehicle', steps: CURRENTVEHICLE_STEPS },
  { matches: under('/home/vehicle/', 'vehicleInfo'), key: 'tour.vehicle-list', steps: VEHICLELIST_STEPS },
  { matches: under('/home/vehicle'), key: 'tour.vehicle-list', steps: VEHICLELIST_STEPS },

  // Account (account-admin-facing pages only — the superowner cross-property
  // screens in this same module deliberately have no tour)
  { matches: under('/home/property/', 'profile'), key: 'tour.profile', steps: PROFILE_STEPS },
  { matches: under('/home/property/', 'ocrAPIKey'), key: 'tour.ocr-api-key', steps: OCRAPIKEY_STEPS },
  { matches: under('/home/property/', 'ocrStats'), key: 'tour.ocr-stats', steps: OCRSTATS_STEPS },
  { matches: under('/home/property/', 'mobileBindingLicense'), key: 'tour.mobile-binding-license', steps: MOBILEBINDINGLICENSE_STEPS },
];

export function findPageTour(url: string): VwPageTourEntry | null {
  return PAGE_TOURS.find(entry => entry.matches(url)) || null;
}
